import { Link as RouterLink } from 'react-router';
import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNoneOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import { NotificationType, type NotificationDto } from '@pd/shared';
import { useMarkNotificationRead } from '../../api/hooks';
import { UserAvatar } from '../../components/UserAvatar';
import { visuallyHidden } from '../../lib/a11y';
import { fmtAgo } from '../../lib/dates';
import { t } from '../../i18n/sq';

export function notificationText(n: NotificationDto) {
  const actor = n.actor?.fullName ?? t.notifications.someone;
  if (n.announcement) return t.notifications.announcement(actor, n.announcement.title);
  const title = n.activity?.title ?? '';
  switch (n.type) {
    case NotificationType.ACTIVITY_CANCELLED:
      return t.notifications.cancelled(actor, title);
    case NotificationType.ACTIVITY_CHANGED:
      return t.notifications.changed(actor, title);
    case NotificationType.ACTIVITY_REPORT_DUE:
      return t.notifications.reportDue(title);
    default:
      return t.notifications.tagged(actor, title);
  }
}

/**
 * Notifications as a list of links to the activity or announcement. Opening one marks it as read. Unread
 * ones are bold with a dot, and the word "I ri" for screen readers.
 */
export function NotificationList({
  items,
  onOpen,
  dense = false,
}: {
  items: NotificationDto[];
  onOpen?: () => void;
  dense?: boolean;
}) {
  const markRead = useMarkNotificationRead();
  return (
    <List disablePadding>
      {items.map((n) => {
        const unread = !n.readAt;
        return (
          <ListItem key={n.id} disablePadding divider>
            <ListItemButton
              component={RouterLink}
              to={
                n.announcement
                  ? `/lajmerimet/${n.announcement.id}`
                  : n.type === NotificationType.ACTIVITY_REPORT_DUE
                    ? `/aktivitetet/${n.activity?.id}/raporti`
                    : `/aktivitetet/${n.activity?.id}`
              }
              onClick={() => {
                if (unread) markRead.mutate(n.id);
                onOpen?.();
              }}
              sx={{
                alignItems: 'flex-start',
                py: dense ? 1.25 : 1.75,
                bgcolor: unread ? 'action.hover' : undefined,
              }}
            >
              <ListItemAvatar sx={{ mt: 0.25, minWidth: 52 }}>
                {n.type === NotificationType.ACTIVITY_REPORT_DUE ? (
                  // A reminder from the platform itself, not from a person.
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'warning.main' }} aria-hidden>
                    <AssignmentIcon />
                  </Avatar>
                ) : (
                  <UserAvatar
                    name={n.actor?.fullName ?? t.notifications.someone}
                    avatarUrl={n.actor?.avatarUrl}
                    size={40}
                    decorative
                  />
                )}
              </ListItemAvatar>
              <ListItemText
                primary={notificationText(n)}
                secondary={fmtAgo(n.createdAt)}
                slotProps={{
                  primary: { sx: { fontWeight: unread ? 600 : 400, overflowWrap: 'anywhere' } },
                  secondary: { sx: { mt: 0.5 } },
                }}
              />
              {unread && (
                <Box sx={{ pt: 1, pl: 1 }}>
                  <Box
                    aria-hidden
                    sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }}
                  />
                  <Box component="span" sx={visuallyHidden}>
                    {t.notifications.unread}
                  </Box>
                </Box>
              )}
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}

export function NotificationsEmpty() {
  return (
    <Box sx={{ textAlign: 'center', py: 5, px: 3 }}>
      <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
      <Typography sx={{ fontWeight: 600 }}>{t.notifications.empty}</Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5 }}>
        {t.notifications.emptyHint}
      </Typography>
    </Box>
  );
}
